import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {FONT_PROXIMA_15} from 'styles/FontConstants';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface LoadingOverlayProps {
  /**
   * Whether or not to fade the overlaid component.
   *
   * @default false
   */
  isFaded?: boolean;
  children: ReactNode;
  /**
   * Whether or not to enable the loading overlay. If `false`, `children` will be returned directly.
   *
   * This is for convenience to the caller so they don't need to have a branch for loading and non-loading
   * render behavior.
   *
   * @default false
   */
  isLoading?: boolean;
  message?: string;
}

const FLOATING_OVERLAY_MESSAGE_HEIGHT = 29;
const WITH_MESSAGE_FLOATING_OVERLAY_HEIGHT = 55 + FLOATING_OVERLAY_MESSAGE_HEIGHT;
const NO_MESSAGE_FLOATING_OVERLAY_HEIGHT = 55;
const FLOATING_OVERLAY_WIDTH = 164;
const FLOATING_OVERLAY_PADDING = 20;

/**
 * Render children with a throbber centered over them. This is particularly useful when you want to render
 * a throbber but also have old or placeholder data to render at the same time, so the user isn't staring
 * at a blank screen while new data is loading.
 *
 * @example
 * <LoadingOverlay>
 *   <div>Hello</div>
 * </LoadingOverlay>
 */
export default function LoadingOverlay({
  children,
  message,
  isFaded = false,
  isLoading = true,
}: LoadingOverlayProps): JSX.Element {
  return (
    <div className={css(loadingOverlayStyles.container)}>
      <div
        className={css(
          loadingOverlayStyles.childrenWrapper,
          isLoading && isFaded && loadingOverlayStyles.fadedChildren
        )}
      >
        {children}
      </div>
      {isLoading && (
        <div className={css(isFaded && loadingOverlayStyles.interactionBlockingOverlay)}>
          <div
            className={css(
              loadingOverlayStyles.floatingOverlay,
              message == null
                ? loadingOverlayStyles.noMessageFloatingOverlay
                : loadingOverlayStyles.withMessageFloatingOverlay
            )}
          >
            <CenteredHorizontalThrobber suppressPadding={true} />
            {message != null && <span className={css(loadingOverlayStyles.message)}>{message}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/** @package */
export const loadingOverlayStyles = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  childrenWrapper: {
    height: '100%',
    width: '100%',
  },
  fadedChildren: {
    opacity: 0.4,
  },
  floatingOverlay: {
    background: WHITE,
    boxShadow: `1px 3px 6px ${GRAY_LIGHT}`,
    display: 'flex',
    flexDirection: 'column',
    left: '50%',
    marginLeft: -FLOATING_OVERLAY_WIDTH / 2,
    padding: FLOATING_OVERLAY_PADDING,
    position: 'absolute',
    top: '50%',
    width: FLOATING_OVERLAY_WIDTH,
  },
  noMessageFloatingOverlay: {
    height: NO_MESSAGE_FLOATING_OVERLAY_HEIGHT,
    marginTop: -NO_MESSAGE_FLOATING_OVERLAY_HEIGHT / 2,
  },
  withMessageFloatingOverlay: {
    height: WITH_MESSAGE_FLOATING_OVERLAY_HEIGHT,
    marginTop: -WITH_MESSAGE_FLOATING_OVERLAY_HEIGHT / 2,
  },
  interactionBlockingOverlay: {
    height: '100%',
    left: '0',
    position: 'absolute',
    top: '0',
    width: '100%',
  },
  message: {
    ...FONT_PROXIMA_15,
    paddingTop: 10,
    textAlign: 'center',
  },
});
